/**
 * Statistics Slice - Centralized statistics management with caching
 * Replaces getUserAssetTree with lightweight count queries
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { apiClient } from '../apiTools/apiClient';
import { config } from '../constants/config';
import { getFlagNames } from '../constants/statisticsFlags';

// Cache duration configuration (in milliseconds)
const CACHE_DURATIONS = {
  // Short cache for frequently changing data
  activeUsers: 1 * 60 * 1000,        // 1 minute
  opportunities: 2 * 60 * 1000,      // 2 minutes

  // Medium cache for moderate change frequency
  portfolio: 5 * 60 * 1000,          // 5 minutes
  assignments: 5 * 60 * 1000,        // 5 minutes
  dashboard: 5 * 60 * 1000,          // 5 minutes

  // Long cache for rarely changing data
  systemOverview: 15 * 60 * 1000,    // 15 minutes
  userStatistics: 10 * 60 * 1000,    // 10 minutes

  // Default cache duration
  default: 5 * 60 * 1000              // 5 minutes
};

/**
 * Check if cached data is still valid
 */
const isCacheValid = (cacheTime, viewType) => {
  if (!cacheTime) return false;

  const duration = CACHE_DURATIONS[viewType] || CACHE_DURATIONS.default;
  return Date.now() - cacheTime < duration;
};

/**
 * Extract role and stat type from viewType
 */
const parseViewType = (viewType) => {
  const parts = viewType.split('_');
  if (parts.length < 2) {
    return { role: 'general', statType: viewType };
  }

  // Handle formats like "bookCreator_dashboard" or "voiceActor_portfolio"
  const role = parts[0];
  const statType = parts.slice(1).join('_');

  return { role, statType };
};

/**
 * Fetch statistics from the server or cache
 */
export const fetchStatistics = createAsyncThunk(
  'statistics/fetch',
  async ({ viewType, flags }, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      // Check if we have valid cached data
      const cachedTime = state.statistics.cacheExpiry[viewType];
      if (isCacheValid(cachedTime, viewType)) {
        console.log(`[STATISTICS] Using cached data for ${viewType}`);
        return {
          viewType,
          fromCache: true,
          timestamp: cachedTime
        };
      }

      console.log(`[STATISTICS] Fetching fresh statistics for ${viewType}`);
      console.log(`[STATISTICS] Flags:`, getFlagNames(flags));

      // Use the new statistics endpoint with BigInt flag support
      // apiClient.get expects (url, urlParams, queryParams, config)
      const response = await apiClient.get(
        config.GET_API_STATISTICS,
        {},  // No URL params to substitute
        {    // Query params
          flags: flags.toString(),
          viewType
        }
      );

      return {
        viewType,
        data: response.data.counts || response.data,
        timestamp: Date.now(),
        fromCache: false
      };
    } catch (error) {
      console.error('[STATISTICS] Error fetching statistics:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Statistics slice definition
 */
const statisticsSlice = createSlice({
  name: 'statistics',
  initialState: {
    // Role-based stat collections
    bookCreator: {},
    voiceActor: {},
    admin: {},
    general: {},

    // Metadata
    lastFetched: {},
    loading: {},
    errors: {},
    cacheExpiry: {}
  },

  reducers: {
    /**
     * Invalidate cache for specific view or all
     */
    invalidateCache: (state, action) => {
      const { viewType, flags } = action.payload || {};

      if (viewType) {
        // Invalidate specific view
        delete state.cacheExpiry[viewType];
        const { role, statType } = parseViewType(viewType);
        if (state[role] && state[role][statType]) {
          delete state[role][statType];
        }
        console.log(`[STATISTICS] Cache invalidated for ${viewType}`);

      } else if (flags) {
        // Invalidate by flags - find all views that use these flags
        Object.keys(state.cacheExpiry).forEach(key => {
          // In a real implementation, we'd check if the view uses these flags
          // For now, we'll invalidate all related views
          delete state.cacheExpiry[key];
        });
        console.log(`[STATISTICS] Cache invalidated for flags:`, getFlagNames(flags));

      } else {
        // Nuclear option - invalidate everything
        state.cacheExpiry = {};
        state.bookCreator = {};
        state.voiceActor = {};
        state.admin = {};
        state.general = {};
        console.log(`[STATISTICS] All cache invalidated`);
      }
    },

    /**
     * Clear error for specific view
     */
    clearError: (state, action) => {
      const { viewType } = action.payload;
      if (viewType && state.errors[viewType]) {
        delete state.errors[viewType];
      }
    }
  },

  extraReducers: (builder) => {
    builder
      // Handle fetch pending
      .addCase(fetchStatistics.pending, (state, action) => {
        const viewType = action.meta.arg.viewType;
        state.loading[viewType] = true;
        state.errors[viewType] = null;
      })

      // Handle fetch success
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        const { viewType, data, timestamp, fromCache } = action.payload;
        const { role, statType } = parseViewType(viewType);

        state.loading[viewType] = false;

        if (!fromCache) {
          // Store fresh data
          if (!state[role]) {
            state[role] = {};
          }
          state[role][statType] = data;
          state.cacheExpiry[viewType] = timestamp;
          state.lastFetched[viewType] = timestamp;

          console.log(`[STATISTICS] Stored fresh data for ${viewType}:`, data);
        }
      })

      // Handle fetch error
      .addCase(fetchStatistics.rejected, (state, action) => {
        const viewType = action.meta.arg.viewType;
        state.loading[viewType] = false;
        state.errors[viewType] = action.payload || 'Failed to fetch statistics';

        console.error(`[STATISTICS] Failed to fetch ${viewType}:`, action.payload);
      })

      // Auto-invalidate cache on related mutations
      // Listen for book-related actions
      .addMatcher(
        (action) => {
          return action.type.includes('books/') &&
            action.type.includes('fulfilled') &&
            !action.type.includes('fetch');
        },
        (state) => {
          // Invalidate book-related statistics
          Object.keys(state.cacheExpiry).forEach(key => {
            if (key.includes('portfolio') ||
              key.includes('library') ||
              key.includes('dashboard')) {
              delete state.cacheExpiry[key];
              console.log(`[STATISTICS] Auto-invalidated ${key} due to book mutation`);
            }
          });
        }
      )

      // Listen for character-related actions
      .addMatcher(
        (action) => {
          return action.type.includes('character') &&
            action.type.includes('fulfilled') &&
            !action.type.includes('fetch');
        },
        (state) => {
          // Invalidate character-related statistics
          Object.keys(state.cacheExpiry).forEach(key => {
            if (key.includes('opportunities') ||
              key.includes('performance')) {
              delete state.cacheExpiry[key];
              console.log(`[STATISTICS] Auto-invalidated ${key} due to character mutation`);
            }
          });
        }
      )

      // Listen for voice actor-related actions
      .addMatcher(
        (action) => {
          return action.type.includes('voiceActor') &&
            action.type.includes('fulfilled') &&
            !action.type.includes('fetch');
        },
        (state) => {
          // Invalidate voice actor statistics
          Object.keys(state.cacheExpiry).forEach(key => {
            if (key.includes('voiceActor')) {
              delete state.cacheExpiry[key];
              console.log(`[STATISTICS] Auto-invalidated ${key} due to voice actor mutation`);
            }
          });
        }
      );
  }
});

// Export actions
export const { invalidateCache, clearError } = statisticsSlice.actions;

// Selectors
export const selectStatistics = (state) => state.statistics;

export const selectStatisticsByView = (viewType) => (state) => {
  const { role, statType } = parseViewType(viewType);
  return state.statistics[role]?.[statType];
};

export const selectStatisticsLoading = (viewType) => (state) => {
  return state.statistics.loading[viewType] || false;
};

export const selectStatisticsError = (viewType) => (state) => {
  return state.statistics.errors[viewType];
};

export const selectCacheStatus = (viewType) => (state) => {
  const cacheTime = state.statistics.cacheExpiry[viewType];
  return {
    isCached: !!cacheTime,
    isValid: isCacheValid(cacheTime, viewType),
    expiresAt: cacheTime ? cacheTime + (CACHE_DURATIONS[viewType] || CACHE_DURATIONS.default) : null
  };
};

// Custom hooks
export const useStatisticsCache = () => {
  const dispatch = useDispatch();

  const invalidateAll = useCallback(() => {
    dispatch(invalidateCache());
  }, [dispatch]);

  const invalidateView = useCallback((viewType) => {
    dispatch(invalidateCache({ viewType }));
  }, [dispatch]);

  const invalidateByFlags = useCallback((flags) => {
    dispatch(invalidateCache({ flags }));
  }, [dispatch]);

  return {
    invalidateAll,
    invalidateView,
    invalidateByFlags
  };
};

// Export reducer
export default statisticsSlice.reducer;
